import { Sparkles } from "lucide-react";
import { FileDropzone } from "@/components/forms";

interface ResumeStepProps {
  file: File | null;
  parsing: boolean;
  parsed: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
  /** Short chips describing what was auto-filled, shown on success. */
  autofilledSummary?: string[];
}

/**
 * Step 1 — the resume drop. Framed as the value proposition ("we do the
 * typing") rather than a plain file field, with a success panel that proves
 * the auto-fill happened.
 */
export function ResumeStep({
  file,
  parsing,
  parsed,
  onSelect,
  onRemove,
  autofilledSummary = [],
}: ResumeStepProps) {
  return (
    <div className="space-y-5">
      <div
        className="flex items-start gap-3 rounded-xl p-4"
        style={{
          background:
            "linear-gradient(120deg, rgba(var(--acc2-rgb),0.06), rgba(var(--acc2-rgb),0.05))",
          border: "1px solid var(--landing-glass-border)",
        }}
      >
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--acc2)]" />
        <p className="text-sm leading-relaxed" style={{ color: "var(--landing-text-secondary)" }}>
          Upload once and our AI reads your resume to pre-fill your experience,
          education, skills, and contact details. You stay in control. Review
          and edit everything before submitting.
        </p>
      </div>

      <FileDropzone
        file={file}
        parsing={parsing}
        parsed={parsed}
        onSelect={onSelect}
        onRemove={onRemove}
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        hint="PDF, DOC, or DOCX · up to 10MB"
        parsingLabel="Reading your resume with AI…"
        parsedTitle="Resume analyzed"
        parsedNote={
          autofilledSummary.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {autofilledSummary.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    background: "rgba(var(--pos-rgb),0.1)",
                    color: "var(--pos)",
                    border: "1px solid rgba(var(--pos-rgb),0.22)",
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          ) : undefined
        }
      />
    </div>
  );
}
